import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PhoneIcon, PhoneXMarkIcon, XMarkIcon, MinusIcon, PlusIcon, ArrowPathIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { esIdCompraValido, useSip } from '../SipContext';
import { PointMascot } from '../PointMascot/PointMascot';
import { CallHistoryModal } from './CallHistoryModal';
import axios from 'axios';
import { APIURL } from '../../configApi/apiConfig';

const STATUS_COLORS = {
    connected: 'bg-emerald-500',
    connecting: 'bg-amber-500',
    disconnected: 'bg-red-500',
    standby: 'bg-slate-400', // el teléfono lo tiene otra pestaña
    error: 'bg-red-500',     // dejó de reintentar: hay que avisar a sistemas
};

const clamp = (val, min, max) => Math.max(min, Math.min(val, max));
const getPointer = (e) => (e.touches ? e.touches[0] : e);

function formatDuration(seconds) {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
}

function getCallView(callState) {
    if (callState === 'active') {
        return {
            label: 'En llamada',
            caption: 'Conversación activa',
            mascotPose: 'peek',
            mascotClass: 'sip-mascot-on-call',
            ringClass: 'border-emerald-300 shadow-emerald-200/70',
        };
    }

    if (callState === 'ringing') {
        return {
            label: 'Timbrando',
            caption: 'Esperando respuesta',
            mascotPose: 'peek',
            mascotClass: 'sip-mascot-ringing',
            ringClass: 'border-amber-300 shadow-amber-200/70',
        };
    }

    return {
        label: 'Listo',
        caption: 'Disponible para llamar',
        mascotPose: 'rest',
        mascotClass: 'sip-mascot-ready',
        ringClass: 'border-slate-200 shadow-slate-200/70',
    };
}

export default function PhonePanel() {
    const {
        status,
        statusText,
        extension,
        callState,
        callSeconds,
        callTarget,
        callClientName,
        trackingId,
        dialerRequest,
        makeCall,
        openDialer,
        hangup,
        contextoGestion,
        reconectar,
        puedeReconectar,
    } = useSip();

    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [showDialPad, setShowDialPad] = useState(false);
    const [dialNumber, setDialNumber] = useState('');
    const [ultimoNumero, setUltimoNumero] = useState(() => {
        try { return localStorage.getItem('sip_last_dialed_number') || ''; } catch (_) { return ''; }
    });
    const [dialContext, setDialContext] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    // Posición y arrastre del panel del celular
    const [position, setPosition] = useState(null);
    const [customPanelPosition, setCustomPanelPosition] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const panelRef = useRef(null);
    const dragStartRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0 });

    // Tamaño del panel del celular (ancho entre 265px y 420px)
    const [panelWidth, setPanelWidth] = useState(320);
    const [isResizing, setIsResizing] = useState(false);
    const isCompact = panelWidth < 290;
    const isLarge = panelWidth > 355;

    // Posición y arrastre del botón flotante (FAB)
    const [fabPosition, setFabPosition] = useState(null);
    const [isFabDragging, setIsFabDragging] = useState(false);
    const fabRef = useRef(null);
    const fabDragStartRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, hasMoved: false });

    // Estado y control de hover para el menú Speed Dial (satélite Historial de Llamadas)
    const [isFabHovered, setIsFabHovered] = useState(false);
    const fabHoverTimeoutRef = useRef(null);

    const handleMouseEnterFab = () => {
        if (fabHoverTimeoutRef.current) {
            clearTimeout(fabHoverTimeoutRef.current);
            fabHoverTimeoutRef.current = null;
        }
        setIsFabHovered(true);
    };

    const handleMouseLeaveFab = () => {
        fabHoverTimeoutRef.current = setTimeout(() => {
            setIsFabHovered(false);
        }, 220);
    };

    useEffect(() => {
        return () => {
            if (fabHoverTimeoutRef.current) clearTimeout(fabHoverTimeoutRef.current);
        };
    }, []);

    // Calcula la posición del panel junto al botón flotante
    const calcularPosicionCercaDelBoton = (fabPos, anchoPanel) => {
        const margin = 10;
        const fabSize = 48;
        const fabX = fabPos ? fabPos.x : (window.innerWidth - fabSize - 20);
        const fabY = fabPos ? fabPos.y : (window.innerHeight - fabSize - 20);

        const panelW = anchoPanel || panelWidth || 320;
        const panelH = panelRef.current?.offsetHeight || 480;

        // Eje X: abre hacia la izquierda si el botón está en la mitad derecha
        const rawX = fabX > window.innerWidth / 2 ? fabX + fabSize - panelW : fabX;
        const maxX = Math.max(margin, window.innerWidth - panelW - margin);
        const posX = clamp(rawX, margin, maxX);

        // Eje Y: abre hacia arriba si está en la mitad inferior
        let posY;
        if (fabY > window.innerHeight / 2) {
            posY = Math.max(margin, fabY - panelH - 12);
        } else {
            const maxY = Math.max(margin, window.innerHeight - panelH - margin);
            posY = Math.min(fabY + fabSize + 12, maxY);
        }

        return { x: posX, y: posY };
    };

    // Arrastre unificado (Mouse + Touch) para el panel del celular
    const handlePanelDragStart = (e) => {
        if (e.button && e.button !== 0) return;
        if (e.target.closest('button') || e.target.closest('input')) return;
        const panel = panelRef.current;
        const pt = getPointer(e);
        if (!panel || !pt) return;

        const rect = panel.getBoundingClientRect();
        dragStartRef.current = {
            startX: pt.clientX,
            startY: pt.clientY,
            originX: rect.left,
            originY: rect.top,
        };
        setIsDragging(true);

        const onMove = (moveEvt) => {
            const m = getPointer(moveEvt);
            if (!m) return;
            const deltaX = m.clientX - dragStartRef.current.startX;
            const deltaY = m.clientY - dragStartRef.current.startY;
            const currentRect = panelRef.current?.getBoundingClientRect() || rect;
            const margin = 8;
            const maxX = Math.max(margin, window.innerWidth - currentRect.width - margin);
            const maxY = Math.max(margin, window.innerHeight - currentRect.height - margin);

            setPosition({
                x: clamp(dragStartRef.current.originX + deltaX, margin, maxX),
                y: clamp(dragStartRef.current.originY + deltaY, margin, maxY),
            });
            setCustomPanelPosition(true);
        };

        const onEnd = () => {
            setIsDragging(false);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onEnd);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onEnd);
    };

    // Control de tamaños predefinidos
    const reducirTamano = () => {
        setPanelWidth((curr) => (curr > 350 ? 320 : 265));
    };

    const agrandarTamano = () => {
        setPanelWidth((curr) => (curr < 290 ? 320 : 390));
    };

    // Redimensionamiento unificado (Mouse + Touch) desde la esquina inferior
    const handleResizeStart = (e) => {
        if (e.button && e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const pt = getPointer(e);
        if (!pt) return;
        const startX = pt.clientX;
        const startW = panelWidth;
        setIsResizing(true);

        const onMove = (moveEvt) => {
            const m = getPointer(moveEvt);
            if (!m) return;
            const deltaX = m.clientX - startX;
            const minW = 260;
            const maxW = Math.min(430, window.innerWidth - 16);
            setPanelWidth(clamp(startW + deltaX, minW, maxW));
        };

        const onEnd = () => {
            setIsResizing(false);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onEnd);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onEnd);
    };

    // Arrastre unificado (Mouse + Touch) del botón flotante (FAB)
    const handleFabDragStart = (e) => {
        if (e.button && e.button !== 0) return;
        if (e.target.closest('button')) return;
        const fab = fabRef.current;
        const pt = getPointer(e);
        if (!fab || !pt) return;

        const rect = fab.getBoundingClientRect();
        fabDragStartRef.current = {
            startX: pt.clientX,
            startY: pt.clientY,
            originX: rect.left,
            originY: rect.top,
            hasMoved: false,
        };

        const onMove = (moveEvt) => {
            const m = getPointer(moveEvt);
            if (!m) return;
            const deltaX = m.clientX - fabDragStartRef.current.startX;
            const deltaY = m.clientY - fabDragStartRef.current.startY;

            if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) {
                fabDragStartRef.current.hasMoved = true;
                setIsFabDragging(true);
                setIsFabHovered(false);
            }

            if (fabDragStartRef.current.hasMoved) {
                const margin = 8;
                const size = 48;
                const maxX = Math.max(margin, window.innerWidth - size - margin);
                const maxY = Math.max(margin, window.innerHeight - size - margin);

                const nextPos = {
                    x: clamp(fabDragStartRef.current.originX + deltaX, margin, maxX),
                    y: clamp(fabDragStartRef.current.originY + deltaY, margin, maxY),
                };
                setFabPosition(nextPos);
                if (open && !customPanelPosition) {
                    setPosition(calcularPosicionCercaDelBoton(nextPos, panelWidth));
                }
            }
        };

        const onEnd = () => {
            setIsFabDragging(false);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onEnd);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onEnd);
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onEnd);
    };

    // Apertura y alternado desde el botón flotante
    const handleFabClick = () => {
        if (fabDragStartRef.current.hasMoved) {
            fabDragStartRef.current.hasMoved = false;
            return;
        }

        // Si no estamos en la página de detalle de cobranza, el celular permanece bloqueado
        if (!isOnDetallePage) {
            return;
        }

        if (open) {
            setOpen(false);
        } else {
            openDialer({
                trackingId: idCompraDetalle,
                target: null,
                clientName: contextoGestion?.clientName ?? null,
            });
            setPosition(calcularPosicionCercaDelBoton(fabPosition, panelWidth));
            setCustomPanelPosition(false);
            setOpen(true);
            setShowDialPad(true);
        }
    };

    // Reajuste automático si cambia el tamaño de la ventana
    useEffect(() => {
        const handleResize = () => {
            const margin = 8;
            if (position) {
                const maxX = Math.max(margin, window.innerWidth - panelWidth - margin);
                const maxY = Math.max(margin, window.innerHeight - 300 - margin);
                setPosition((prev) => (prev ? {
                    x: clamp(prev.x, margin, maxX),
                    y: clamp(prev.y, margin, maxY),
                } : null));
            }
            if (fabPosition) {
                const size = 48;
                const maxX = Math.max(margin, window.innerWidth - size - margin);
                const maxY = Math.max(margin, window.innerHeight - size - margin);
                setFabPosition((prev) => (prev ? {
                    x: clamp(prev.x, margin, maxX),
                    y: clamp(prev.y, margin, maxY),
                } : null));
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [position, fabPosition, panelWidth]);

    // Contexto de cobranza y estado de llamada
    const isOnDetallePage = /^\/detalle-cobranza\/[^/]+/.test(location.pathname);
    const idCompraDetalle = isOnDetallePage ? contextoGestion?.idCompra ?? null : null;
    const isRegistered = extension !== null;
    const isInCall = callState !== 'idle';
    const view = getCallView(callState);
    const displayNumber = showDialPad && !isInCall ? dialNumber : callTarget;

    const resolverIdCompra = (numero) => {
        const numTrim = String(numero || '').trim();
        // 1. Prioridad: Si dialContext tiene un trackingId explícito (asignado por historial de llamadas o búsqueda inversa)
        if (dialContext && esIdCompraValido(dialContext.trackingId)) {
            if (dialContext.origenExterno || dialContext.porCliente || numTrim === dialContext.lastNumber) {
                return dialContext.trackingId;
            }
        }
        // 2. Respaldo: Si estamos en la página de cobranza de un cliente y no se ha elegido otro número
        if (esIdCompraValido(idCompraDetalle)) return idCompraDetalle;
        return null;
    };
    const idCompraMarcador = resolverIdCompra(dialNumber.trim());

    const appendDigit = (digit) => {
        setDialNumber((value) => `${value}${digit}`.slice(0, 10));
    };

    const removeDigit = () => {
        setDialNumber((value) => value.slice(0, -1));
    };

    const handleNewCall = () => {
        setOpen(true);
        setShowDialPad(true);
        setDialNumber(dialContext?.lastNumber || ultimoNumero || '');
    };

    const redialTarget = ultimoNumero || dialContext?.lastNumber || '';

    const handleRedial = () => {
        if (!redialTarget) return;
        setDialNumber(redialTarget);
    };

    const handleDial = async (numeroEspecifico) => {
        let target = (typeof numeroEspecifico === 'string' ? numeroEspecifico : dialNumber).trim();
        if (!target) {
            if (redialTarget) {
                target = redialTarget;
                setDialNumber(redialTarget);
            } else {
                return;
            }
        }

        setUltimoNumero(target);
        try { localStorage.setItem('sip_last_dialed_number', target); } catch (_) {}

        const clientNameToUse = dialContext?.clientName || (isOnDetallePage ? contextoGestion?.clientName : null) || null;
        const enviado = await makeCall(target, resolverIdCompra(target), {
            clientName: clientNameToUse,
        });
        if (enviado) setShowDialPad(false);
    };

    // Solicitud externa de marcado
    useEffect(() => {
        if (!dialerRequest) return;

        setPosition(calcularPosicionCercaDelBoton(fabPosition, panelWidth));
        setCustomPanelPosition(false);
        setOpen(true);
        setShowDialPad(true);
        setDialNumber(dialerRequest.target || '');
        setDialContext({
            trackingId: dialerRequest.trackingId,
            clientName: dialerRequest.clientName,
            lastNumber: dialerRequest.target || '',
            porCliente: true,
        });
    }, [dialerRequest]);

    // Sincronizar número y contexto del cliente cuando se inicia o recibe una llamada
    useEffect(() => {
        if (callTarget) {
            setUltimoNumero(callTarget);
            setDialNumber(callTarget);
            try { localStorage.setItem('sip_last_dialed_number', callTarget); } catch (_) {}
            setDialContext((prev) => ({
                ...prev,
                lastNumber: callTarget,
                clientName: callClientName || prev?.clientName || null,
                trackingId: trackingId || prev?.trackingId || null,
                porCliente: true,
            }));
        } else if (callClientName) {
            setDialContext((prev) => ({
                ...prev,
                clientName: callClientName,
            }));
        }
    }, [callTarget, callClientName, trackingId]);

    // Búsqueda inversa automática: si se digitan 9 o 10 dígitos en el marcador,
    // consultar a qué cliente e idCompra pertenece el número
    useEffect(() => {
        const numDigits = String(dialNumber || '').replace(/\D/g, '');
        if (numDigits.length < 9 || isInCall || !showDialPad) return;
        if (dialContext?.lastNumber === numDigits && dialContext?.trackingId) return;

        const timer = setTimeout(async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const res = await axios.get(APIURL.buscarClientePorTelefono(numDigits), { headers });
                if (res?.data?.encontrado && res?.data?.idCompra) {
                    setDialContext({
                        trackingId: res.data.idCompra,
                        clientName: res.data.cliente,
                        lastNumber: numDigits,
                        porCliente: true,
                        origenExterno: true,
                    });
                }
            } catch (_) {}
        }, 400);

        return () => clearTimeout(timer);
    }, [dialNumber, isInCall, showDialPad, dialContext]);

    // Abrir automáticamente el celular cuando se inicia o recibe una llamada para ver el progreso
    useEffect(() => {
        if (callState === 'ringing' || callState === 'active') {
            setPosition((prev) => (customPanelPosition && prev ? prev : calcularPosicionCercaDelBoton(fabPosition, panelWidth)));
            setOpen(true);
            setShowDialPad(false);
        }
    }, [callState, fabPosition, panelWidth, customPanelPosition]);

    // Teclado físico
    useEffect(() => {
        const handleKeyDown = (event) => {
            const targetTag = event.target?.tagName?.toLowerCase();
            const isTyping = targetTag === 'input' || targetTag === 'textarea';

            if (!open || isTyping) return;

            // Si está en llamada activa: Enter o Escape cuelga la llamada de inmediato
            if (isInCall) {
                if (event.key === 'Enter' || event.key === 'Escape') {
                    event.preventDefault();
                    hangup();
                }
                return;
            }

            if (!showDialPad) return;

            if (/^[0-9]$/.test(event.key)) {
                event.preventDefault();
                appendDigit(event.key);
                return;
            }

            if (event.key === 'Backspace') {
                event.preventDefault();
                removeDigit();
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                handleDial();
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dialContext, dialNumber, isInCall, open, showDialPad, idCompraDetalle, contextoGestion, redialTarget, hangup]);

    // Tamaño de la mascota según contexto
    const mascotSize = (showDialPad && !isInCall)
        ? (isCompact ? 44 : isLarge ? 58 : 50)
        : (isCompact ? 85 : isLarge ? 130 : 105);

    const posEfectiva = position || calcularPosicionCercaDelBoton(fabPosition, panelWidth);

    return (
        <>
            <style>{`
                .sip-phone-shell {
                    background:
                        radial-gradient(circle at 25% 18%, rgba(255, 215, 0, 0.18), transparent 28%),
                        radial-gradient(circle at 82% 12%, rgba(235, 42, 27, 0.18), transparent 24%),
                        linear-gradient(160deg, #111827 0%, #172554 48%, #0f172a 100%);
                }
                .sip-screen-glow {
                    background:
                        linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.92)),
                        radial-gradient(circle at center, rgba(45,54,137,0.12), transparent 60%);
                }
                .sip-screen-glow::-webkit-scrollbar {
                    width: 4px;
                }
                .sip-screen-glow::-webkit-scrollbar-thumb {
                    background-color: rgba(148, 163, 184, 0.5);
                    border-radius: 9999px;
                }
                .sip-mascot-wrap .mascota svg .pierna,
                .sip-mascot-wrap .mascota svg .brazo {
                    transform-box: fill-box;
                }
                .sip-mascot-wrap .mascota svg .pierna-i { transform-origin: 60% 0%; }
                .sip-mascot-wrap .mascota svg .pierna-d { transform-origin: 40% 0%; }
                .sip-mascot-wrap .mascota svg .brazo-i  { transform-origin: 95% 95%; }
                .sip-mascot-wrap .mascota svg .brazo-d  { transform-origin: 5% 95%; }
                @keyframes sipRingingLean {
                    0%, 100% { transform: translateX(0) rotate(-1deg); }
                    25% { transform: translateX(-4px) rotate(-4deg); }
                    75% { transform: translateX(4px) rotate(3deg); }
                }
                @keyframes sipTalkBob {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-4px); }
                }
                @keyframes sipArmTalkLeft {
                    0%, 100% { transform: rotate(22deg) translateY(-2px); }
                    50% { transform: rotate(32deg) translateY(-8px); }
                }
                @keyframes sipArmTalkRight {
                    0%, 100% { transform: rotate(-28deg) translateY(-10px); }
                    50% { transform: rotate(-38deg) translateY(-14px); }
                }
                @keyframes sipPulseRing {
                    0%, 100% { transform: scale(1); opacity: .55; }
                    50% { transform: scale(1.08); opacity: .95; }
                }
                @keyframes sipPhoneFloat {
                    0%, 100% { transform: rotate(-22deg) translateY(0); }
                    50% { transform: rotate(-25deg) translateY(-2px); }
                }
                @keyframes sipBlink {
                    0%, 88%, 100% { transform: scaleY(1); }
                    92%, 96% { transform: scaleY(.08); }
                }
                .sip-mascot-ringing { animation: sipRingingLean .8s ease-in-out infinite; }
                .sip-mascot-ready svg .mascota-eye {
                    transform-box: fill-box;
                    transform-origin: center;
                    animation: sipBlink 3.2s ease-in-out infinite;
                }
                .sip-mascot-on-call { animation: sipTalkBob 1.05s ease-in-out infinite; }
                .sip-mascot-on-call svg .brazo-i { animation: sipArmTalkLeft .8s ease-in-out infinite; }
                .sip-mascot-on-call svg .brazo-d { animation: sipArmTalkRight .8s ease-in-out infinite; }
                .sip-pulse-ring { animation: sipPulseRing 1.2s ease-in-out infinite; }
                .sip-call-handset { animation: sipPhoneFloat 1s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) {
                    .sip-mascot-ringing,
                    .sip-mascot-ready svg .mascota-eye,
                    .sip-mascot-on-call,
                    .sip-mascot-on-call svg .brazo-i,
                    .sip-mascot-on-call svg .brazo-d,
                    .sip-pulse-ring,
                    .sip-call-handset {
                        animation: none !important;
                    }
                }
            `}</style>

            {open && (
                <div
                    ref={panelRef}
                    style={{
                        top: `${posEfectiva.y}px`,
                        left: `${posEfectiva.x}px`,
                        bottom: 'auto',
                        right: 'auto',
                        width: `min(${panelWidth}px, calc(100vw - 1rem))`,
                    }}
                    className={`fixed z-[60] max-h-[calc(100dvh-2.5rem)] rounded-[2rem] bg-slate-950 p-2 shadow-2xl border border-slate-700 flex flex-col transition-shadow ${
                        isDragging ? 'shadow-indigo-500/40 ring-2 ring-indigo-500/60' : ''
                    } ${isResizing ? 'ring-2 ring-indigo-400/80 shadow-2xl' : ''}`}
                >
                    <div className="sip-phone-shell relative rounded-[1.55rem] flex flex-col max-h-full overflow-hidden text-white">
                        {/* Cabecera / Barra de arrastre y controles de tamaño */}
                        <div
                            onMouseDown={handlePanelDragStart}
                            onTouchStart={handlePanelDragStart}
                            onDoubleClick={() => {
                                setPosition(calcularPosicionCercaDelBoton(fabPosition, panelWidth));
                                setCustomPanelPosition(false);
                            }}
                            className={`px-3 sm:px-4 pt-3 pb-2 flex items-center justify-between gap-1 select-none ${
                                isDragging ? 'cursor-grabbing' : 'cursor-grab'
                            }`}
                            title="Haz clic y arrastra para mover por la pantalla (Doble clic para restablecer junto al botón)"
                        >
                            <div className="flex items-center gap-1.5 sm:gap-2">
                                <span className="text-[11px] font-bold tracking-wide">
                                    {isRegistered ? `EXT ${extension}` : 'SIN EXT'}
                                </span>
                                <span className="flex items-center gap-0.5 text-slate-400 px-1 py-0.5 rounded bg-white/5" title="Arrastrar">
                                    <span className="w-1 h-1 rounded-full bg-slate-400" />
                                    <span className="w-1 h-1 rounded-full bg-slate-400" />
                                    <span className="w-1 h-1 rounded-full bg-slate-400" />
                                </span>
                            </div>

                            <div className="flex items-center gap-1.5 sm:gap-2">
                                {/* Controles de tamaño (Pequeño / Normal / Grande) */}
                                <div className="flex items-center bg-white/10 rounded-lg p-0.5 border border-white/10 shadow-xs" title="Ajustar tamaño del celular">
                                    <button
                                        type="button"
                                        onClick={reducirTamano}
                                        disabled={panelWidth <= 265}
                                        className="p-1 rounded text-slate-300 hover:text-white hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition cursor-pointer"
                                        title="Hacer más pequeño (S)"
                                    >
                                        <MinusIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPanelWidth(320)}
                                        className="px-1 text-[10px] font-bold text-slate-300 hover:text-white transition cursor-pointer"
                                        title="Clic para restaurar tamaño medio (M)"
                                    >
                                        {isCompact ? 'S' : isLarge ? 'L' : 'M'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={agrandarTamano}
                                        disabled={panelWidth >= 410}
                                        className="p-1 rounded text-slate-300 hover:text-white hover:bg-white/15 disabled:opacity-30 disabled:hover:bg-transparent transition cursor-pointer"
                                        title="Hacer más grande (L)"
                                    >
                                        <PlusIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                    </button>
                                </div>

                                {/* Botón de Historial de Llamadas */}
                                {isRegistered && (
                                    <button
                                        type="button"
                                        onClick={() => setIsHistoryOpen(true)}
                                        className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/15 bg-white/5 border border-white/10 transition cursor-pointer flex items-center justify-center"
                                        title="Historial de llamadas (CDR)"
                                    >
                                        <ClipboardDocumentListIcon className="w-3.5 h-3.5" />
                                    </button>
                                )}

                                <span className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS[status]}`} />
                                <button
                                    type="button"
                                    onClick={() => { setOpen(false); setDialContext(null); }}
                                    className="p-1 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition cursor-pointer"
                                    title="Cerrar panel"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Pantalla del teléfono */}
                        <div className="mx-2 sm:mx-3 mb-2 sm:mb-3 rounded-[1.35rem] sip-screen-glow text-slate-900 overflow-y-auto max-h-[calc(100dvh-7rem)] flex flex-col justify-between">
                            {/* 1. SECCIÓN SUPERIOR: Info, Número, Cliente, Timer */}
                            <div className={`${isCompact ? 'px-2.5 pt-2.5' : isLarge ? 'px-5 pt-4' : 'px-4 pt-3'} text-center`}>
                                <div className="flex items-center justify-between px-1">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                                        {isInCall ? view.label : (showDialPad ? 'Marcador' : view.label)}
                                    </p>
                                    <div className="flex items-center gap-1.5">
                                        {showDialPad && !isInCall && redialTarget && (
                                            <button
                                                type="button"
                                                onClick={handleRedial}
                                                className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-1.5 py-0.5 rounded-md flex items-center gap-1 cursor-pointer transition shadow-2xs"
                                                title={`Cargar último número llamado: ${redialTarget}`}
                                            >
                                                <ArrowPathIcon className="w-3 h-3 text-indigo-600" />
                                                <span>Redial</span>
                                            </button>
                                        )}
                                        {showDialPad && !isInCall && (
                                            <button
                                                type="button"
                                                onClick={() => setShowDialPad(false)}
                                                className="text-[10px] font-bold text-slate-400 hover:text-slate-700 cursor-pointer transition"
                                                title="Ocultar teclado numérico"
                                            >
                                                Ocultar
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Campo de número / entrada */}
                                <div className="relative mt-1 flex items-center justify-center">
                                    <input
                                        type="text"
                                        className={`w-full text-center ${isCompact ? 'text-xl' : isLarge ? 'text-3xl' : 'text-2xl'} font-black text-slate-950 tracking-wide bg-transparent border-none outline-none placeholder-slate-400`}
                                        value={displayNumber}
                                        onChange={(e) => {
                                            if (!isInCall && showDialPad) {
                                                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                setDialNumber(val);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            e.stopPropagation();
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleDial();
                                            }
                                        }}
                                        placeholder="Sin número"
                                        readOnly={isInCall || !showDialPad}
                                    />
                                    {showDialPad && !isInCall && dialNumber && (
                                        <button
                                            type="button"
                                            onClick={() => setDialNumber('')}
                                            className="absolute right-1 p-1 text-slate-400 hover:text-slate-700 rounded-full transition cursor-pointer"
                                            title="Limpiar número"
                                        >
                                            <XMarkIcon className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>

                                {/* Subtítulo / Estado o Cronómetro durante llamada */}
                                {isInCall ? (
                                    <div className="mt-0.5 flex items-center justify-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                        <span className={`${isCompact ? 'text-base' : 'text-lg'} font-black font-mono text-emerald-700`}>
                                            {formatDuration(callSeconds)}
                                        </span>
                                    </div>
                                ) : (
                                    <p className="mt-0.5 text-[11px] font-semibold text-slate-500">
                                        {showDialPad ? 'Digite el número o use el teclado' : statusText}
                                    </p>
                                )}

                                {/* Badge con nombre del cliente y compra si está disponible */}
                                {(() => {
                                    const nombreCliente = callClientName || dialContext?.clientName || (isOnDetallePage ? contextoGestion?.clientName : null);
                                    const idCompraBadge = trackingId || dialContext?.trackingId || (isOnDetallePage ? idCompraDetalle : null);
                                    if (!nombreCliente && !idCompraBadge) return null;

                                    return (
                                        <div
                                            className="mt-1.5 mx-auto w-11/12 text-center text-[10px] sm:text-xs font-black uppercase tracking-wide text-white bg-blue-600 shadow-xs py-1 px-2.5 rounded-lg truncate leading-tight"
                                            title={nombreCliente || `Compra #${idCompraBadge}`}
                                        >
                                            <span>{nombreCliente || 'Cliente Identificado'}</span>
                                            {idCompraBadge && (
                                                <span className="block text-[9px] font-mono opacity-90 font-bold normal-case tracking-normal">
                                                    Compra #{idCompraBadge}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Teléfono en otra pestaña o sin conexión tras varios intentos (el motivo va en el subtítulo) */}
                                {(status === 'standby' || status === 'error') && !isInCall && puedeReconectar && (
                                    <div className={`mt-1.5 mx-auto w-11/12 rounded-lg px-2.5 py-1.5 text-[10px] sm:text-xs leading-snug ${
                                        status === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-slate-100 text-slate-600 border border-slate-200'
                                    }`}>
                                        <button
                                            type="button"
                                            onClick={reconectar}
                                            className="inline-flex items-center gap-1 font-bold text-blue-700 hover:text-blue-900 cursor-pointer"
                                        >
                                            <ArrowPathIcon className="w-3.5 h-3.5" />
                                            {status === 'standby' ? 'Usar aquí' : 'Reintentar'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* 2. SECCIÓN MEDIA: Mascota animada */}
                            <div className={`relative flex items-center justify-center ${
                                showDialPad && !isInCall
                                    ? 'py-1 min-h-[48px]'
                                    : (isCompact ? 'pt-2 pb-1 min-h-[95px]' : isLarge ? 'pt-4 pb-3 min-h-[145px]' : 'pt-3 pb-2 min-h-[120px]')
                            }`}>
                                {isInCall && (
                                    <div className={`absolute ${isCompact ? 'w-24 h-24' : isLarge ? 'w-40 h-40' : 'w-32 h-32'} rounded-full border-8 ${view.ringClass} sip-pulse-ring`} />
                                )}

                                <div className={`sip-mascot-wrap relative z-10 ${view.mascotClass}`}>
                                    <PointMascot
                                        pose={view.mascotPose}
                                        size={mascotSize}
                                        cubreOjos={false}
                                    />

                                    {callState === 'active' && (
                                        <div className="sip-call-handset absolute right-0 top-12 w-6 h-9 rounded-[0.7rem] bg-slate-950 shadow-xl border border-slate-700">
                                            <div className="absolute left-1/2 top-1 h-0.5 w-3 -translate-x-1/2 rounded-full bg-slate-500" />
                                            <PhoneIcon className="absolute left-1/2 top-1/2 w-3 h-3 -translate-x-1/2 -translate-y-1/2 text-emerald-300" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 3. SECCIÓN INFERIOR: Teclado y Botones de acción */}
                            <div className={`${isCompact ? 'px-2.5 pb-2.5' : isLarge ? 'px-5 pb-4' : 'px-4 pb-3'}`}>
                                {showDialPad && !isInCall ? (
                                    <div>
                                        {/* Grid del teclado 3x4: 1-9, C (Limpiar), 0, ⌫ (Borrar) */}
                                        <div className={`grid grid-cols-3 ${isCompact ? 'gap-1' : isLarge ? 'gap-2' : 'gap-1.5'}`}>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                                                <button
                                                    key={digit}
                                                    type="button"
                                                    onClick={() => appendDigit(String(digit))}
                                                    className={`${isCompact ? 'h-8 text-base' : isLarge ? 'h-11 text-xl' : 'h-9 text-lg'} rounded-xl bg-white border border-slate-200 text-slate-900 font-black hover:bg-slate-100 active:scale-95 transition cursor-pointer shadow-2xs`}
                                                >
                                                    {digit}
                                                </button>
                                            ))}
                                            {/* Fila 4: Redial / Limpiar, 0, Borrar un dígito */}
                                            <button
                                                type="button"
                                                onClick={dialNumber ? () => setDialNumber('') : handleRedial}
                                                disabled={!dialNumber && !redialTarget}
                                                className={`${isCompact ? 'h-8 text-[11px]' : isLarge ? 'h-11 text-sm' : 'h-9 text-xs'} rounded-xl ${
                                                    !dialNumber && redialTarget
                                                        ? 'bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100'
                                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                                                } disabled:opacity-40 font-bold transition cursor-pointer flex items-center justify-center gap-1 shadow-2xs`}
                                                title={dialNumber ? 'Limpiar número' : (redialTarget ? `Redial: ${redialTarget}` : 'Sin número previo')}
                                            >
                                                {!dialNumber && redialTarget ? (
                                                    <>
                                                        <ArrowPathIcon className="w-3.5 h-3.5 text-indigo-600" />
                                                        <span className="text-[10px] font-black">Redial</span>
                                                    </>
                                                ) : (
                                                    'C'
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => appendDigit('0')}
                                                className={`${isCompact ? 'h-8 text-base' : isLarge ? 'h-11 text-xl' : 'h-9 text-lg'} rounded-xl bg-white border border-slate-200 text-slate-900 font-black hover:bg-slate-100 active:scale-95 transition cursor-pointer shadow-2xs`}
                                            >
                                                0
                                            </button>
                                            <button
                                                type="button"
                                                onClick={removeDigit}
                                                disabled={!dialNumber}
                                                className={`${isCompact ? 'h-8 text-xs' : isLarge ? 'h-11 text-base' : 'h-9 text-sm'} rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-600 font-black transition cursor-pointer flex items-center justify-center`}
                                                title="Borrar dígito"
                                            >
                                                ⌫
                                            </button>
                                        </div>

                                        {dialNumber.trim() && !idCompraMarcador && (
                                            <p className="mt-1.5 rounded-lg bg-red-50 border border-red-200 px-2.5 py-1 text-[10px] font-bold text-red-700 leading-tight text-center">
                                                Sin idCompra: marque desde la gestión del cliente.
                                            </p>
                                        )}

                                        {/* Botón principal de Llamar / Remarcar */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (!dialNumber.trim() && redialTarget) {
                                                    setDialNumber(redialTarget);
                                                    handleDial(redialTarget);
                                                } else {
                                                    handleDial();
                                                }
                                            }}
                                            title={
                                                !dialNumber.trim() && redialTarget
                                                    ? `Remarcar ${redialTarget}`
                                                    : (idCompraMarcador ? `Compra #${idCompraMarcador}` : 'Sin idCompra: no se puede llamar')
                                            }
                                            disabled={(!dialNumber.trim() && !redialTarget) || !isRegistered || (!idCompraMarcador && !resolverIdCompra(redialTarget))}
                                            className={`mt-2 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-black rounded-xl ${
                                                isCompact ? 'h-8.5 text-xs' : isLarge ? 'h-11 text-base' : 'h-9.5 text-sm'
                                            } transition-all shadow-md shadow-emerald-500/20 active:scale-98 cursor-pointer`}
                                        >
                                            <PhoneIcon className="w-4 h-4" />
                                            <span>
                                                {!dialNumber.trim() && redialTarget
                                                    ? `Redial (${redialTarget})`
                                                    : 'Llamar'}
                                            </span>
                                        </button>
                                    </div>
                                ) : isInCall ? (
                                    <button
                                        type="button"
                                        onClick={hangup}
                                        title="Colgar llamada (presiona Enter o clic)"
                                        className={`w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white ${
                                            isCompact ? 'py-2 text-xs' : isLarge ? 'py-3 text-base' : 'py-2.5 text-sm'
                                        } font-black rounded-2xl transition-colors shadow-lg shadow-red-500/25 active:scale-98 cursor-pointer`}
                                    >
                                        <PhoneXMarkIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                        <span>Colgar llamada</span>
                                        <span className="text-[10px] font-mono bg-white/20 px-1.5 py-0.5 rounded opacity-80" title="Tecla Enter">Enter</span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleNewCall}
                                        disabled={!isRegistered}
                                        className={`w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:text-slate-500 text-white ${
                                            isCompact ? 'py-2 text-xs' : isLarge ? 'py-3 text-base' : 'py-2.5 text-sm'
                                        } font-black rounded-2xl transition-colors shadow-lg shadow-emerald-500/25 active:scale-98 cursor-pointer`}
                                    >
                                        <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                        <span>Abrir marcador</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Tirador para redimensionar en la esquina inferior derecha */}
                        <div
                            onMouseDown={handleResizeStart}
                            onTouchStart={handleResizeStart}
                            className="absolute bottom-1 right-1 w-5 h-5 flex items-center justify-center cursor-nwse-resize z-30 text-slate-400 hover:text-indigo-400 active:text-indigo-300 transition-colors select-none"
                            title="Arrastra para redimensionar el celular libremente"
                        >
                            <svg className="w-3.5 h-3.5 opacity-60 hover:opacity-100 transition-opacity" viewBox="0 0 16 16" fill="currentColor">
                                <circle cx="13" cy="13" r="1.5" />
                                <circle cx="13" cy="8" r="1.5" />
                                <circle cx="8" cy="13" r="1.5" />
                                <circle cx="13" cy="3" r="1.5" />
                                <circle cx="8" cy="8" r="1.5" />
                                <circle cx="3" cy="13" r="1.5" />
                            </svg>
                        </div>
                    </div>
                </div>
            )}

            {/* Botón flotante Speed Dial (arrastrable por toda la pantalla con mouse o touch) */}
            <div
                ref={fabRef}
                style={
                    fabPosition
                        ? {
                            top: `${fabPosition.y}px`,
                            left: `${fabPosition.x}px`,
                            bottom: 'auto',
                            right: 'auto',
                        }
                        : undefined
                }
                onMouseDown={handleFabDragStart}
                onTouchStart={handleFabDragStart}
                onMouseEnter={handleMouseEnterFab}
                onMouseLeave={handleMouseLeaveFab}
                onDoubleClick={() => {
                    setFabPosition(null);
                    if (open && !customPanelPosition) {
                        setPosition(calcularPosicionCercaDelBoton(null, panelWidth));
                    }
                }}
                className={`fixed z-[60] select-none flex ${
                    fabPosition && fabPosition.y < 130 ? 'flex-col' : 'flex-col-reverse'
                } items-center gap-3 ${
                    !fabPosition ? 'bottom-6 right-6' : ''
                } ${isFabDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            >
                {/* 1. Botón Principal del Celular (Sin title nativo para evitar tooltips del SO cortados) */}
                <div
                    onClick={handleFabClick}
                    className={`relative w-12 h-12 rounded-full shadow-2xl flex items-center justify-center transition-all duration-200 select-none ${
                        isOnDetallePage
                            ? 'bg-gradient-to-br from-blue-500 to-blue-700 hover:scale-105 hover:shadow-blue-500/50 active:scale-95 cursor-pointer'
                            : 'bg-slate-900 border border-slate-700/80 hover:bg-slate-800 hover:scale-105 cursor-pointer shadow-slate-900/60'
                    } ${isFabDragging ? 'scale-110 shadow-2xl ring-4 ring-blue-400/50' : ''}`}
                >
                    {isOnDetallePage && !isFabDragging && (
                        <span className="absolute inset-0 rounded-full animate-ping bg-blue-400 opacity-30 pointer-events-none" />
                    )}
                    <PhoneIcon className="w-6 h-6 text-white relative z-10 pointer-events-none" />
                    <span className={`absolute top-0 right-0 w-3 h-3 rounded-full border-2 border-white ${STATUS_COLORS[status]} z-10`} />
                </div>

                {/* 2. Botón Satélite Speed Dial: Solo muestra el icono, y al pasar el mouse sobre el icono sale el nombre */}
                {isFabHovered && !isFabDragging && (
                    <div
                        className="relative group/satelite animate-in fade-in zoom-in-75 slide-in-from-bottom-2 duration-200"
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                    >
                        {/* Tooltip flotante hacia la izquierda que aparece ÚNICAMENTE al hacer hover sobre este icono satélite */}
                        <div
                            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
                            className="absolute right-full mr-3 top-1/2 -translate-y-1/2 text-white text-[11px] font-bold py-1.5 px-3 rounded-xl shadow-2xl whitespace-nowrap border border-slate-700/80 pointer-events-none flex items-center gap-1.5 opacity-0 group-hover/satelite:opacity-100 transition-opacity duration-150 z-50 select-none"
                        >
                            <span>📋</span>
                            <span>Historial de Llamadas</span>
                            {extension && (
                                <span className="text-[10px] text-indigo-300 font-mono font-bold bg-indigo-500/20 px-1.5 py-0.5 rounded">
                                    EXT {extension}
                                </span>
                            )}
                        </div>

                        {/* Botón Circular Satélite: Fondo índigo sólido e icono blanco nítido garantizado */}
                        <button
                            type="button"
                            style={{ backgroundColor: '#4338ca', color: '#ffffff' }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsHistoryOpen(true);
                            }}
                            className="w-11 h-11 rounded-full text-white shadow-2xl border-2 border-indigo-300/40 flex items-center justify-center transition-all duration-150 hover:scale-110 active:scale-95 cursor-pointer hover:brightness-110"
                        >
                            <ClipboardDocumentListIcon
                                className="w-5 h-5 text-white"
                                style={{ stroke: '#ffffff', strokeWidth: 2 }}
                            />
                        </button>
                    </div>
                )}
            </div>

            {/* Modal de Historial de Llamadas */}
            <CallHistoryModal
                isOpen={isHistoryOpen}
                onClose={() => setIsHistoryOpen(false)}
                extension={extension}
                canCall={isOnDetallePage}
                onSelectPhone={isOnDetallePage ? (data) => {
                    const telefono = typeof data === 'object' ? data?.telefono : data;
                    const clientName = typeof data === 'object' ? data?.cliente : null;
                    const trackingId = typeof data === 'object' ? data?.idCompra : null;

                    if (!telefono) return;
                    setDialNumber(telefono);
                    setDialContext({
                        trackingId: trackingId || null,
                        clientName: clientName || null,
                        lastNumber: telefono,
                        porCliente: true,
                        origenExterno: true,
                    });
                    setOpen(true);
                    setShowDialPad(true);
                } : null}
            />
        </>
    );
}
